import React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import Loader from 'components/loader/Loader';

import { SIGN_IN_PATHS } from './constants';
import { useSignInSettings } from './settings';
import { SignInScreen } from './screens/SignInScreen';
import { CodeScreen } from './screens/CodeScreen';
import { ForgotScreen } from './screens/ForgotScreen';
import { ResetScreen } from './screens/ResetScreen';
import { InviteScreen } from './screens/InviteScreen';
import {
  ClientScreen, ExpiredScreen, PausedScreen, SignedOutScreen, TeamRemovedScreen, WelcomeScreen,
} from './screens/StateScreens';

/** The current page (a withRouter component, or one that takes the route's props). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TClassic = React.ComponentType<any>;

/**
 * The new screen while the new sign-in is switched on, the current page
 * (exactly as it was) while it's off. Nothing shows while the switch loads.
 */
const Switched = ({ classic: Classic, screen, route }: { classic: TClassic; screen: React.ReactNode; route: RouteComponentProps }) => {
  const settings = useSignInSettings();
  if (!settings) {
    return <Loader />;
  }
  return settings.newSignIn ? <>{screen}</> : <Classic {...route} />;
};

/** /sign-in */
export const SignInRoute = ({ classic, route }: { classic: TClassic; route: RouteComponentProps }) => (
  <Switched classic={classic} route={route} screen={<SignInScreen />} />
);

/** /forgot-password */
export const ForgotRoute = ({ classic, route }: { classic: TClassic; route: RouteComponentProps }) => (
  <Switched classic={classic} route={route} screen={<ForgotScreen />} />
);

/** /new-password: a ?token= link only exists while the switch is on, so it always gets the new screen. */
export const ResetRoute = ({ classic, route }: { classic: TClassic; route: RouteComponentProps }) =>
  new URLSearchParams(route.location.search).get('token')
    ? <ResetScreen />
    : <Switched classic={classic} route={route} screen={<ResetScreen />} />;

const STEPS: Record<string, React.ComponentType> = {
  code: CodeScreen,
  expired: ExpiredScreen,
  paused: PausedScreen,
  'team-removed': TeamRemovedScreen,
  client: ClientScreen,
  'signed-out': SignedOutScreen,
  welcome: WelcomeScreen,
  invite: InviteScreen,
};

/** /sign-in/:step: the screens only the new sign-in leads to. */
export const SignInStepRoute = ({ match }: RouteComponentProps<{ step: string }>) => {
  const Step = STEPS[match.params.step];
  return Step ? <Step /> : <Redirect to={SIGN_IN_PATHS.SIGN_IN} />;
};
